import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import {
  Trash2, Sparkles, Zap, BookOpen, FileText, Volume2,
  Download, Lightbulb, Share2, Link2, MessageCircle,
} from 'lucide-react';
import { Button } from '../ui/button';

const IMPORT_SOURCE_ICONS = {
  youtube_import: '▶',
  image_ocr: '📷',
  slides_import: '📑',
};
const getImportIcon = (src) => IMPORT_SOURCE_ICONS[src] || '🎵';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Ask-the-AI-Tutor quick-chat button.
 *
 * Lightest-weight implementation of "quick chat while working on a
 * lecture": navigates to /study-buddy with the lecture's subject_id
 * pre-selected via the URL. StudyBuddyPage reads `?focus=<subject_id>`
 * on mount and applies it to the focus scope selector so the AI
 * immediately grounds in this lecture's class.
 *
 * Why URL-param + redirect instead of a slide-out drawer in-place: the
 * StudyBuddyPage already owns the voice/persona/RAG machinery — duping
 * it inside a drawer would split state across two render trees. URL
 * params keep one source of truth and let users deep-link back via
 * browser back/forward.
 */
function AskTutorButton({ lecture }) {
  const navigate = useNavigate();
  if (!lecture) return null;
  const handleClick = () => {
    const params = new URLSearchParams();
    if (lecture.subject_id) params.set('focus', lecture.subject_id);
    params.set('lecture', lecture.id);
    navigate(`/study-buddy?${params.toString()}`);
  };
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={handleClick}
      className="rounded-xl gap-2 border-[#002FA7]/30 text-[#7E9EFF] hover:text-white hover:bg-[#002FA7]/10"
      data-testid={`ask-tutor-${lecture.id}`}
    >
      <MessageCircle className="w-4 h-4" />
      Ask AI Tutor
    </Button>
  );
}

const downloadTxt = (lecture) => {
  const blob = new Blob([lecture.transcript], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${lecture.title || 'transcript'}.txt`;
  a.click();
  URL.revokeObjectURL(url);
};

export const LectureCard = ({
  lecture, index, subject, isSelected,
  onToggleSelect, onDelete, onExportPDF,
  onGenerateLearningBytes, onShare, onShareStudyKit,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
    >
      <div className="p-4 sm:p-6 isp-card transition-all duration-300" data-testid={`lecture-card-${lecture.id}`}>
        <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-4">
          <div className="flex-1 min-w-0 w-full">
            <div className="flex items-center gap-2 mb-2">
              {subject && (
                <div
                  className="px-3 py-1 rounded-lg text-xs font-medium text-white"
                  style={{ backgroundColor: subject.color }}
                >
                  {subject.name}
                </div>
              )}
            </div>
            <h3 className="text-lg sm:text-xl font-semibold text-[var(--isp-heading)] mb-1">{lecture.title}</h3>
            {lecture.topic && (
              <p className="text-sm text-[var(--isp-body)] flex items-center gap-2">
                {lecture.import_source && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-violet-500/15 text-violet-400 border border-violet-500/20">
                    {getImportIcon(lecture.import_source)} {lecture.topic}
                  </span>
                )}
                {!lecture.import_source && lecture.topic}
              </p>
            )}
            <p className="text-xs font-mono text-[var(--isp-muted)] mt-2">
              {format(new Date(lecture.date), 'PPp')}
            </p>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleSelect}
              className="rounded-xl flex-1 sm:flex-none text-[var(--isp-body)] hover:text-[var(--isp-heading)] hover:bg-[var(--isp-glass-inner)]"
              data-testid={`view-lecture-${lecture.id}`}
            >
              {isSelected ? 'Hide' : 'View'}
            </Button>
            <Button aria-label="Delete"
              variant="ghost"
              size="icon"
              onClick={() => onDelete(lecture.id)}
              className="rounded-xl text-red-400 hover:text-red-300 hover:bg-red-500/10"
              data-testid={`delete-lecture-${lecture.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <AnimatePresence>
          {isSelected && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-4 mt-4 pt-4 border-t border-[var(--isp-glass-border)]"
              data-testid={`lecture-details-${lecture.id}`}
            >
              {lecture.summary && (
                <div className="p-4 bg-purple-500/10 rounded-xl border border-purple-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-purple-400" />
                    <h4 className="font-medium text-purple-300">AI Summary</h4>
                  </div>
                  <p className="text-sm text-purple-200/80">{lecture.summary}</p>
                </div>
              )}

              {lecture.key_points && lecture.key_points.length > 0 && (
                <div>
                  <h4 className="font-medium text-[var(--isp-heading)] mb-2 flex items-center gap-2">
                    <Zap className="w-4 h-4 text-yellow-400" />
                    Key Points
                  </h4>
                  <ul className="space-y-2">
                    {lecture.key_points.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-[var(--isp-body)]">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-purple-400 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {lecture.notes && (
                <div>
                  <h4 className="font-medium text-[var(--isp-heading)] mb-2 flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-blue-400" />
                    Notes
                  </h4>
                  <p className="text-sm text-[var(--isp-body)] whitespace-pre-wrap bg-[var(--isp-glass)] backdrop-blur-xl p-4 rounded-xl">
                    {lecture.notes}
                  </p>
                </div>
              )}

              {lecture.transcript && (
                <div data-testid={`lecture-transcript-${lecture.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-[var(--isp-heading)] flex items-center gap-2">
                      <FileText className="w-4 h-4 text-emerald-400" />
                      Full Transcript
                    </h4>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => downloadTxt(lecture)}
                      className="gap-1.5 rounded-lg border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 text-xs h-7"
                      data-testid={`download-transcript-${lecture.id}`}
                    >
                      <Download className="w-3 h-3" />
                      Download .txt
                    </Button>
                  </div>
                  <div className="text-sm text-[var(--isp-body)] whitespace-pre-wrap bg-[var(--isp-glass)] backdrop-blur-xl p-4 rounded-xl max-h-60 overflow-y-auto leading-relaxed">
                    {lecture.transcript}
                  </div>
                </div>
              )}

              {lecture.has_audio && (
                <div data-testid={`lecture-audio-${lecture.id}`}>
                  <h4 className="font-medium text-[var(--isp-heading)] mb-2 flex items-center gap-2">
                    <Volume2 className="w-4 h-4 text-blue-400" />
                    Audio Recording
                  </h4>
                  <div className="p-3 bg-[var(--isp-glass)] backdrop-blur-xl rounded-xl border border-[var(--isp-glass-border)]">
                    <audio
                      controls
                      src={`${API}/uploads/audio/${lecture.audio_file_id || ''}/stream`}
                      className="w-full h-10"
                      preload="metadata"
                    />
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-2">
                <AskTutorButton lecture={lecture} />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onExportPDF(lecture)}
                  className="rounded-xl gap-2 border-[#CCFF00]/30 text-[#CCFF00] hover:text-[#CCFF00] hover:bg-[#CCFF00]/10"
                  data-testid={`export-pdf-${lecture.id}`}
                >
                  <Download className="w-4 h-4" />
                  Export PDF
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onGenerateLearningBytes(lecture.id)}
                  className="rounded-xl gap-2 border-[var(--isp-glass-border)] text-[var(--isp-body)] hover:text-[var(--isp-heading)] hover:bg-[var(--isp-glass-inner)]"
                  data-testid={`generate-bytes-${lecture.id}`}
                >
                  <Lightbulb className="w-4 h-4" />
                  Generate Learning Bytes
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onShare(lecture)}
                  className="rounded-xl gap-2 border-[var(--isp-glass-border)] text-[var(--isp-body)] hover:text-[var(--isp-heading)] hover:bg-[var(--isp-glass-inner)]"
                  data-testid={`share-lecture-${lecture.id}`}
                >
                  <Share2 className="w-4 h-4" />
                  Share
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onShareStudyKit(lecture)}
                  className="rounded-xl gap-2 border-green-500/30 text-green-300 hover:text-green-200 hover:bg-green-500/10"
                  data-testid={`share-study-kit-${lecture.id}`}
                >
                  <Link2 className="w-4 h-4" />
                  Share Study Kit
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};
